// @ts-nocheck
import FVector from '@data/geometry/FVector.js';
import { getLoadingStatus, LoadingStatus } from 'store/LoadingStore';
import Grid from '../Geodata/grid.js';
import usePersistentStore, { getVectorialScalarMode } from '../Gui/store/persistent/persistentStore.js';
import { Frame } from './Frame.js';

export class SensorPoint {
    private floor;
    ID = -1;
    index = -1;
    private distanceToWallProject = -1;
    private distanceToRoofProject = -1;
    private distanceToOverallProject = -1;

    private center: FVector;

    neighbors6: SensorPoint[] = [];
    neighbours8: SensorPoint[] = [];
    neighbors26: SensorPoint[] = [];

    constructor(center: FVector, ID = -1, index = -1) {
        this.ID = ID;
        this.index = index;

        this.center = center;

        const z = center.z - Grid.gridRes / 2;
        this.floor = Math.round(z / Grid.gridRes);
    }

    isNull() {
        return this.ID === -1;
    }

    getID() {
        return this.ID;
    }

    getCenter() {
        return this.center;
    }

    getFloor() {
        return this.floor;
    }

    setDistanceToProjectSketch(wallDistance: number, roofDistance: number, overallDistance: number) {
        this.distanceToWallProject = wallDistance;
        this.distanceToRoofProject = roofDistance;
        this.distanceToOverallProject = overallDistance;
    }

    getDistanceToProjectSketchWall() {
        return this.distanceToWallProject;
    }

    getDistanceToProjectsketchRoof() {
        return this.distanceToRoofProject;
    }

    getDistanceToProjectSketchOverall() {
        return this.distanceToOverallProject;
    }
}

export class SensorData {
    private readonly sensorPoint: SensorPoint;

    private scalarValue;
    private vectorialValue;

    constructor(sensorPoint: SensorPoint, value: number, direction: FVector) {
        this.sensorPoint = sensorPoint;
        this.scalarValue = value;
        this.vectorialValue = direction;
    }

    getSensorPoint() {
        return this.sensorPoint;
    }

    getPosition() {
        return this.sensorPoint.getCenter();
    }

    getNeighbours(sensorDatas: SensorData[]): SensorData[] {
        // Devo usare un set per farlo in modo efficiente, se gli id dei neighbours sono dentro al set di sensorDatasID allora ci sono:
        const neighbourPoints = this.sensorPoint.neighbors6;
        return sensorDatas.filter((sd) => neighbourPoints.some((np) => np.getID() === sd.getSensorPoint().getID()));
    }

    computeNeighboursScore(radius: number, sensorDatas: SensorData[]): number {
        const myIndex = this.sensorPoint.index;
        const decomposedIndices = Grid.indexToXYZ(myIndex);
        const x = decomposedIndices[0];
        const y = decomposedIndices[1];
        const z = decomposedIndices[2];
        if (z == 0) return 1;
        const lowerFloor = z - 1;
        const MAIN_FLOOR = this.sensorPoint.getFloor();
        let n = 0; // number of active nehgbours
        let N = 0; // number of neighbours

        for (let xTmp = x - radius; xTmp <= x + radius; xTmp++) {
            if (xTmp < 0 || xTmp > Grid.gridX) continue;
            for (let yTmp = y - radius; yTmp <= y + radius; yTmp++) {
                if (yTmp < 0 || yTmp > Grid.gridY) continue;
                const tmpIndex = Grid.xyzToIndex(xTmp, yTmp, lowerFloor);
                if (tmpIndex < 0 || tmpIndex >= Grid.sensorPoints.length) continue;

                const sensorPointID = Grid.sensorPoints[tmpIndex].getID();
                const LOWER_FLOOR = Grid.sensorPoints[tmpIndex].getFloor();
                if (MAIN_FLOOR - 1 != LOWER_FLOOR) {
                    // console.error("Error in the floor computation", MAIN_FLOOR, LOWER_FLOOR);
                    continue;
                }
                if (sensorPointID === -1) continue;
                N++; //increase number of neighbours
                if (!sensorDatas.find((sensorData) => sensorData.getID() === sensorPointID)) continue;
                n++; // increase number of active neighbours
            }
        }
        return n / N;
    }

    getScalarValue(): number {
        return this.scalarValue;
    }

    getVectorialResult() {
        return this.vectorialValue.clone();
    }

    /**
     *
     * @deprecated. Use getVectorialResult() instead
     */
    getDirection() {
        return this.getVectorialResult();
    }

    computeRotations(): { alpha: Number; beta: Number; rotAngleS: number } {
        let e1 = new FVector(1, 0, 0);
        // final FVector e2 = new FVector(0, 1, 0);
        let e3 = new FVector(0, 0, 1);
        // For a loose orientation (only towards a given direction but with a free spin)
        // an angle and a 3D axis around which to make the rotation are sufficient
        let tmpDirection = this.getVectorialResult();
        let rotAxisS = e3.cross(tmpDirection); // this is also the lines of Nodes of my Euler ANgles (see below)
        rotAxisS.normalize();
        const rotAngleS = e3.angleBetween(tmpDirection);

        // For a more constrained orientation I am doing a rotation with Z-X'-Z� Euler
        // angles, setting as an additional constrain gamma=0 (Z-X'), see on
        // http://en.wikipedia.org/wiki/Euler_angles
        // this ensures to have a two-rotations (orientation and tilt) orienting process
        // maintaining the top and bottom edges of the Box in the x-y axis
        // lines of Nodes is taken from above

        // Compute alfa for Z rotation
        let alpha = e1.angleBetween(rotAxisS);
        // find out the sign of the angle and invert if necessary, see on
        // http://stackoverflow.com/questions/5188561/signed-angle-between-two-3d-vectors-with-same-origin-within-the-same-plane-reci
        let v3 = e1.cross(rotAxisS); // order matters
        if (v3.dot(e3) < 0) alpha = -alpha;

        // Compute beta for X' rotation
        let beta = e3.angleBetween(tmpDirection);
        // find out the sign of the angle and invert if necessary, see on
        // http://stackoverflow.com/questions/5188561/signed-angle-between-two-3d-vectors-with-same-origin-within-the-same-plane-reci
        v3 = e3.cross(tmpDirection); // order matters
        if (v3.dot(rotAxisS) < 0) beta = -beta;

        return { alpha, beta, rotAngleS };
    }

    ///////////////////////
    // Setters & Getters //
    ///////////////////////

    setScalarValue(scalarValue: number) {
        if (this.getScalarValue() != scalarValue) {
            if (scalarValue == Infinity || scalarValue < 0) scalarValue = 0;
            this.scalarValue = scalarValue;
        }
    }

    setVectorialValue(vectorialValue: FVector) {
        if (
            this.vectorialValue.x != vectorialValue.x ||
            this.vectorialValue.y != vectorialValue.y ||
            this.vectorialValue.z != vectorialValue.z
        ) {
            if (vectorialValue.mag() == Infinity || vectorialValue.mag() < 0) vectorialValue = new FVector();
            this.vectorialValue = vectorialValue;
        }
    }

    /**
     *
     * @deprecated Sensors in output from the pipeline are always active. This function always returns true
     */
    isActive() {
        return true;
    }

    getID() {
        return this.sensorPoint.getID();
    }

    getIndex() {
        return this.sensorPoint.index;
    }
}
